import { ResponseError, JSONApiResponse, FetchError, UnknownError } from '@/api/generator/runtime';
import { instanceOfPtw100MaintenanceModeException } from '@/api/generator';
import type * as Generated from '@/api/generator';

/**
 * Every generated error-response DTO name, harvested from the generated `instanceOf*` guards -
 * the same export set `dtoName` is derived from, so this cannot drift from `.name`. Grows
 * automatically on every `pnpm generateApi`.
 */
export type KnownErrorName = keyof typeof Generated extends infer K
  ? K extends `instanceOf${infer N extends `ErrorResponseOfPtw100${string}Exception`}`
    ? N
    : never
  : never;

/** The generated interface for a given name, read off the generated `FromJSON` return type. */
type BodyOf<N extends KnownErrorName> = ReturnType<(typeof Generated)[`${N}FromJSON`]>;

/**
 * Closed discriminated union of every known backend exception response, derived - not
 * hand-maintained - from the generated client's own exports.
 *
 * The `name: N` intersection is load-bearing. `ResponseError` declares `name` as
 * `GetN<R> | 'ResponseError'`, so without it every member also admits the literal
 * `'ResponseError'` and stays structurally related to the undeclared-status
 * `ResponseError<JSONApiResponse>`. That member then survives narrowing and collapses `.error`
 * to `{}` at every call site behind a type guard. Pinning the discriminant to exactly `N`
 * matches runtime - a known error's `name` is always its `dtoName` - and keeps the union
 * genuinely disjoint.
 */
export type KnownApiError = {
  [N in KnownErrorName]: ResponseError<JSONApiResponse<BodyOf<N>, N>> & { name: N };
}[KnownErrorName];

/** The closed DTO union plus the non-DTO failure variants `handle()` can produce,
 * all discriminated by the same literal `.name`. */
export type AnyApiError =
  | KnownApiError
  | ResponseError<JSONApiResponse>
  | FetchError
  | UnknownError;

/**
 * Short, readable handles for the generated literal error names.
 *
 * Only names the generator actually emits can appear here, and it emits an
 * `ErrorResponseOf*`/`instanceOf*` pair only once some operation declares that response.
 * `satisfies` catches a backend rename or removal - an existing value stops being a valid
 * `KnownErrorName` - as a compile error on the exact alias affected. `ErrorNamesWithoutAlias`
 * below catches a backend addition, naming the alias that is missing.
 */
export const ApiError = {
  badRequest: 'ErrorResponseOfPtw100BadRequestException',
  unauthorized: 'ErrorResponseOfPtw100UnauthorizedException',
  forbidden: 'ErrorResponseOfPtw100ForbiddenException',
  maintenanceMode: 'ErrorResponseOfPtw100MaintenanceModeException',
  notFound: 'ErrorResponseOfPtw100NotFoundException',
  conflict: 'ErrorResponseOfPtw100ConflictException',
  unprocessableContent: 'ErrorResponseOfPtw100UnprocessableContentException',
  internalServer: 'ErrorResponseOfPtw100InternalServerException',
  serviceUnavailable: 'ErrorResponseOfPtw100ServiceUnavailableException',
  deviceMeasurement: 'ErrorResponseOfPtw100DeviceMeasurementException',
  badGateway: 'ErrorResponseOfPtw100BadGatewayException',
  setupCombinations: 'ErrorResponseOfPtw100SetupCombinationsException',
  csoPortalBadGateway: 'ErrorResponseOfPtw100CsoPortalBadGatewayException',
  csoPortalServiceUnavailable: 'ErrorResponseOfPtw100CsoPortalServiceUnavailableException',
} as const satisfies Record<string, KnownErrorName>;

type Unaliased = Exclude<KnownErrorName, (typeof ApiError)[keyof typeof ApiError]>;

/** Names every generated error name that `ApiError` above is missing an alias for. */
export type ErrorNamesWithoutAlias<T extends never = Unaliased> = T;

/**
 * Entry guard into the discriminated union: after this, `switch (error.name)` or
 * `error.name === ApiError.xxx` narrows precisely, so no per-type guard is needed.
 */
export function isApiFailure(error: unknown): error is AnyApiError {
  return (
    error instanceof ResponseError || error instanceof FetchError || error instanceof UnknownError
  );
}

/**
 * A declared response with a real DTO body, as opposed to a fetch-level failure or an
 * undeclared-status `ResponseError`. The `instanceof` is load-bearing: `FetchError` and
 * `UnknownError` also have a `.name` other than `'ResponseError'`.
 */
export function isKnownApiError(error: unknown): error is KnownApiError {
  return error instanceof ResponseError && error.name !== 'ResponseError';
}

/**
 * 5xx by status, including the 502/504 an intermediary can produce without the OpenAPI spec
 * ever declaring them. Deliberately not a type guard and deliberately overlapping with the
 * `.name` checks: it classifies a status class, not a schema, so a genuine
 * `ErrorResponseOfPtw100InternalServerException` satisfies both.
 */
export function isServerFailure(error: unknown): boolean {
  return error instanceof ResponseError && error.response.status >= 500;
}

/**
 * Both `Ptw100ForbiddenException` and `Ptw100MaintenanceModeException` map to HTTP 403, and an
 * operation's spec can declare only one of them, so `error.name` reflects the declared schema,
 * not the actual body. The body's `$type` is the real discriminator.
 */
export function isMaintenanceModeError(error: AnyApiError): boolean {
  return isKnownApiError(error) && instanceOfPtw100MaintenanceModeException(error.error?.exception);
}

/**
 * Exhaustiveness helper: call with the whole `error` in a `switch (error.name)` default branch.
 * A name without a case fails to compile there. The throw only covers values that bypass the
 * type system at runtime.
 */
export function assertNever(value: never): never {
  const runtimeValue: unknown = value;
  const name =
    runtimeValue && typeof runtimeValue === 'object' && 'name' in runtimeValue
      ? String(runtimeValue.name)
      : String(runtimeValue);
  throw new Error(`Unhandled case: ${name}`);
}
